import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { User } from '@vsolv/core/users/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { PaginationConfig } from '@vsolv/vectors/table';
import { Audit } from '@vsolv/core/audit/domain';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { Claim } from '@wsphere/warranties/domain';
import { WarrantyWebService } from '../../../warranty/services';
import { ClaimService } from '../../services';
import { AUDIT_CATEGORIES, AuditEntryView, AuditNames, toAuditEntryView } from './claim-audit-entry';

interface AuditDay {
  label: string;
  entries: AuditEntryView[];
}

interface AuditState {
  isLoading: boolean;
  hasError: boolean;
  days: AuditDay[];
  totalItems: number;
  pagination: PaginationConfig | null;
}

/** Changes shown before "Show all". */
const VISIBLE_CHANGES = 4;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-audit',
  templateUrl: './claim-audit.page.html',
  standalone: false,
})
export class ClaimAuditPage {
  constructor(
    private claimSvc: ClaimService,
    private warrantySvc: WarrantyWebService,
    private toastSvc: ToastService
  ) {}

  /** Inside another card: no frame or header of its own. */
  @Input() embedded = false;

  readonly categories = AUDIT_CATEGORIES;
  readonly visibleChanges = VISIBLE_CHANGES;

  // One stream, so a filter change sends one request.
  private readonly query$ = new BehaviorSubject<{ entityType: string | null; page: number; limit: number }>({
    entityType: null,
    page: 1,
    limit: 10,
  });
  readonly entityType$ = this.query$.pipe(
    map(query => query.entityType),
    distinctUntilChanged()
  );

  /** Shown while the next page loads, so the list doesn't blank out. */
  private last: AuditState = this.emptyState(true);

  private readonly users = new Map<string, User.Model | null>();
  private readonly expandedChanges = new Set<string>();

  readonly claim$ = this.claimSvc.getClaim();

  readonly state$ = combineLatest([this.claim$, this.query$]).pipe(
    switchMap(([claim, { entityType, page, limit }]) => {
      if (!claim) return of(this.emptyState(false));

      return from(this.claimSvc.listAudit(claim.id, { page, limit, entityType })).pipe(
        switchMap(response =>
          from(this.loadUsers(response.items.map(item => item.changedBy))).pipe(map(() => response))
        ),
        map(
          (response): AuditState => ({
            isLoading: false,
            hasError: false,
            days: this.groupByDay(response.items.map(item => toAuditEntryView(item, this.namesOf(claim)))),
            totalItems: response.meta.totalItems,
            pagination: {
              totalItems: response.meta.totalItems,
              itemsPerPage: response.meta.itemsPerPage,
              currentPage: response.meta.currentPage,
              pageSizes: [10, 20, 50],
            },
          })
        ),
        catchError(() => {
          this.toastSvc.show({ title: 'Unable to load the audit trail', text: 'Please try again.', type: 'error' });
          return of({ ...this.emptyState(false), hasError: true });
        }),
        map(state => (this.last = state)),
        startWith({ ...this.last, isLoading: true, hasError: false })
      );
    }),
    shareReplay(1)
  );

  setEntityType(entityType: string | null) {
    if (entityType === this.query$.value.entityType) return;
    // Another kind of record: start from the skeleton, not the previous kind's entries.
    this.expandedChanges.clear();
    this.last = this.emptyState(true);
    this.query$.next({ ...this.query$.value, entityType, page: 1 });
  }

  refresh() {
    this.query$.next({ ...this.query$.value });
  }

  onPageChange(event: { currentPage: number; itemsPerPage: number }) {
    this.query$.next({ ...this.query$.value, page: event.currentPage, limit: event.itemsPerPage });
  }

  user(entry: AuditEntryView) {
    return entry.changedBy ? (this.users.get(entry.changedBy) ?? null) : null;
  }

  actorName(entry: AuditEntryView) {
    if (entry.isSystem) return 'System';
    return this.user(entry)?.displayName || 'Unknown user';
  }

  initials(entry: AuditEntryView) {
    const name = this.user(entry)?.displayName?.trim();
    if (!name) return '?';
    const parts = name.split(/\s+/);
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  visibleChangesOf(entry: AuditEntryView) {
    return this.isExpanded(entry) ? entry.changes : entry.changes.slice(0, VISIBLE_CHANGES);
  }

  isExpanded(entry: AuditEntryView) {
    return this.expandedChanges.has(entry.id);
  }

  toggleChanges(entry: AuditEntryView) {
    if (!this.expandedChanges.delete(entry.id)) this.expandedChanges.add(entry.id);
  }

  trackById(_index: number, item: { id: string }) {
    return item.id;
  }

  trackByLabel(_index: number, day: AuditDay) {
    return day.label;
  }

  /** Each person once per page, kept across pages; the system has no user. */
  private async loadUsers(ids: (string | null)[]) {
    const missing = [...new Set(ids)].filter(
      (id): id is string => !!id && id !== Audit.SYSTEM_ACTOR && !this.users.has(id)
    );
    const users = await Promise.all(missing.map(id => this.warrantySvc.getAuditUser(id)));
    missing.forEach((id, index) => this.users.set(id, users[index]));
  }

  /** Coverage titles and job names from the loaded claim, so entries read by name rather than id. */
  private namesOf(claim: Claim.Model): AuditNames {
    const coverages = new Map<string, string>();
    const jobs = new Map<string, string>();
    for (const coverage of claim.warranty?.policy?.coverages ?? []) coverages.set(coverage.id, coverage.title);
    for (const item of claim.items ?? []) {
      if (item.coverage) coverages.set(item.coverage.id, item.coverage.title);
      jobs.set(item.id, item.description || item.coverage?.title || 'Untitled job');
    }
    return { coverages, jobs };
  }

  private groupByDay(entries: AuditEntryView[]): AuditDay[] {
    const days: AuditDay[] = [];
    for (const entry of entries) {
      const label = this.dayLabel(new Date(entry.changedAt));
      const day = days[days.length - 1];
      if (day?.label === label) day.entries.push(entry);
      else days.push({ label, entries: [entry] });
    }
    return days;
  }

  private dayLabel(date: Date) {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return formatDate(date, date.getFullYear() === today.getFullYear() ? 'EEEE, MMM d' : 'EEEE, MMM d, y', 'en-US');
  }

  private emptyState(isLoading: boolean): AuditState {
    return { isLoading, hasError: false, days: [], totalItems: 0, pagination: null };
  }
}
